import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  Zap,
  Brain,
  Settings,
  Play,
  Pause,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  BarChart3,
  Clock,
  MapPin,
  Smartphone,
  Monitor,
  Tablet,
  Calendar,
  ArrowUp,
  ArrowDown,
  Sparkles,
  MousePointer
} from 'lucide-react';

interface BidStrategy {
  id: string;
  name: string;
  type: 'MANUAL' | 'TARGET_CPA' | 'TARGET_ROAS' | 'MAXIMIZE_CLICKS' | 'MAXIMIZE_CONVERSIONS';
  targetValue?: number;
  maxBid?: number;
  minBid?: number;
  adjustmentRules: AdjustmentRule[];
}

interface AdjustmentRule {
  id: string;
  type: 'DEVICE' | 'LOCATION' | 'TIME' | 'AUDIENCE';
  condition: string;
  adjustmentPercent: number;
  enabled: boolean;
}

interface BidOptimizerProps {
  campaignId: string;
  onApply: (strategy: BidStrategy) => void;
}

const BidOptimizer: React.FC<BidOptimizerProps> = ({ campaignId, onApply }) => {
  const [activeStrategy, setActiveStrategy] = useState<'manual' | 'auto'>('manual');
  const [targetCPA, setTargetCPA] = useState(25);
  const [targetROAS, setTargetROAS] = useState(400);
  const [maxCPC, setMaxCPC] = useState(3);
  const [minCPC, setMinCPC] = useState(0.5);
  const [loading, setLoading] = useState(false);
  const [optimization, setOptimization] = useState<'conversions' | 'clicks' | 'value'>('conversions');
  
  // Harmonogram bidów
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [schedule, setSchedule] = useState({
    monday: { enabled: true, multiplier: 1.0 },
    tuesday: { enabled: true, multiplier: 1.0 },
    wednesday: { enabled: true, multiplier: 1.0 },
    thursday: { enabled: true, multiplier: 1.0 },
    friday: { enabled: true, multiplier: 1.2 },
    saturday: { enabled: true, multiplier: 0.8 },
    sunday: { enabled: true, multiplier: 0.8 }
  });

  // Modyfikatory urządzeń
  const [deviceBidAdjustments, setDeviceBidAdjustments] = useState({
    desktop: 0,
    mobile: -20,
    tablet: -10
  });

  // Modyfikatory lokalizacji
  const [locationAdjustments, setLocationAdjustments] = useState([
    { location: 'Gdańsk', adjustment: 20, enabled: true },
    { location: 'Warszawa', adjustment: 15, enabled: true },
    { location: 'Kraków', adjustment: 10, enabled: true },
    { location: 'Pozostałe', adjustment: -10, enabled: false }
  ]);

  // Symulacja wyników optymalizacji
  const [simulationResults, setSimulationResults] = useState({
    estimatedClicks: 0,
    estimatedCost: 0,
    estimatedConversions: 0,
    estimatedCPA: 0,
    confidence: 0
  });

  // Symuluj wyniki przy zmianie parametrów
  useEffect(() => {
    simulateResults();
  }, [targetCPA, targetROAS, maxCPC, optimization]);

  const simulateResults = () => {
    // Symulacja bazowana na strategii
    const baseClicks = 1000;
    const baseCost = 2000;
    const baseConversions = 50;
    
    let multiplier = 1;
    if (optimization === 'clicks') multiplier = 1.3;
    if (optimization === 'conversions') multiplier = 0.9;
    if (optimization === 'value') multiplier = 0.85;
    
    const estimatedClicks = Math.round(baseClicks * multiplier * (maxCPC / 2));
    const estimatedCost = baseCost * (targetCPA / 25);
    const estimatedConversions = Math.round(baseConversions * (25 / targetCPA));
    
    setSimulationResults({
      estimatedClicks,
      estimatedCost,
      estimatedConversions,
      estimatedCPA: estimatedCost / estimatedConversions,
      confidence: Math.min(95, 60 + Math.random() * 35)
    });
  };

  const applyOptimization = async () => {
    setLoading(true);
    
    // Przygotuj strategię
    const strategy: BidStrategy = {
      id: Date.now().toString(),
      name: activeStrategy === 'auto' ? 'AI Optimization' : 'Manual Bidding',
      type: optimization === 'conversions' ? 'MAXIMIZE_CONVERSIONS' : 
            optimization === 'clicks' ? 'MAXIMIZE_CLICKS' : 
            'TARGET_ROAS',
      targetValue: optimization === 'value' ? targetROAS : targetCPA,
      maxBid: maxCPC,
      minBid: minCPC,
      adjustmentRules: [
        ...Object.entries(deviceBidAdjustments).map(([device, adjustment]) => ({
          id: `device-${device}`,
          type: 'DEVICE' as const,
          condition: device,
          adjustmentPercent: adjustment,
          enabled: true
        })),
        ...locationAdjustments.filter(l => l.enabled).map(loc => ({
          id: `location-${loc.location}`,
          type: 'LOCATION' as const,
          condition: loc.location,
          adjustmentPercent: loc.adjustment,
          enabled: true
        }))
      ]
    };
    
    // Symulacja API call
    setTimeout(() => {
      onApply(strategy);
      setLoading(false);
    }, 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
              <Brain className="w-6 h-6" />
            </div>
            Optymalizator stawek AI
          </h2>
          <p className="text-white/60 mt-1">Inteligentne zarządzanie stawkami w czasie rzeczywistym</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setActiveStrategy('manual')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeStrategy === 'manual'
                ? 'bg-purple-600/20 border border-purple-500/30 text-purple-400'
                : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Ręczne
          </button>
          <button
            onClick={() => setActiveStrategy('auto')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeStrategy === 'auto'
                ? 'bg-purple-600/20 border border-purple-500/30 text-purple-400'
                : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
            }`}
          >
            <Sparkles className="w-4 h-4 inline mr-2" />
            AI Auto
          </button>
        </div>
      </div>

      {/* Strategia optymalizacji */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { id: 'conversions', name: 'Maksymalizuj konwersje', icon: Target, color: 'purple' },
          { id: 'clicks', name: 'Maksymalizuj kliknięcia', icon: MousePointer, color: 'blue' },
          { id: 'value', name: 'Maksymalizuj wartość', icon: DollarSign, color: 'green' }
        ].map((opt) => (
          <motion.button
            key={opt.id}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setOptimization(opt.id as any)}
            className={`p-4 rounded-xl border transition-all ${
              optimization === opt.id
                ? opt.color === 'purple' ? 'bg-purple-900/30 border-purple-500/30' :
                  opt.color === 'blue' ? 'bg-blue-900/30 border-blue-500/30' :
                  'bg-green-900/30 border-green-500/30'
                : 'bg-white/5 border-white/10 hover:bg-white/10'
            }`}
          >
            <opt.icon className={`w-6 h-6 mb-2 ${
              optimization === opt.id 
                ? opt.color === 'purple' ? 'text-purple-400' :
                  opt.color === 'blue' ? 'text-blue-400' :
                  'text-green-400'
                : 'text-white/60'
            }`} />
            <p className="font-medium text-white">{opt.name}</p>
            <p className="text-xs text-white/40 mt-1">
              {opt.id === 'conversions' && 'Optymalizuj pod kątem sprzedaży'}
              {opt.id === 'clicks' && 'Zwiększ ruch na stronie'}
              {opt.id === 'value' && 'Maksymalizuj ROI'}
            </p>
          </motion.button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Parametry strategii */}
        <div className="space-y-4">
          <div className="p-6 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-400" />
              Cele optymalizacji
            </h3>
            
            <div className="space-y-4">
              {optimization !== 'clicks' && (
                <div>
                  <label className="text-sm text-white/60 mb-2 block">
                    {optimization === 'value' ? 'Docelowy ROAS (%)' : 'Docelowy CPA (PLN)'}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={optimization === 'value' ? targetROAS : targetCPA}
                      onChange={(e) => optimization === 'value' 
                        ? setTargetROAS(Number(e.target.value))
                        : setTargetCPA(Number(e.target.value))
                      }
                      className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-purple-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40">
                      {optimization === 'value' ? '%' : 'PLN'}
                    </span>
                  </div>
                </div>
              )}
              
              <div>
                <label className="text-sm text-white/60 mb-2 block">
                  Maksymalne CPC (PLN)
                </label>
                <input
                  type="number"
                  value={maxCPC}
                  onChange={(e) => setMaxCPC(Number(e.target.value))}
                  step="0.1"
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              
              <div>
                <label className="text-sm text-white/60 mb-2 block">
                  Minimalne CPC (PLN)
                </label>
                <input
                  type="number"
                  value={minCPC}
                  onChange={(e) => setMinCPC(Number(e.target.value))}
                  step="0.1"
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Modyfikatory urządzeń */}
          <div className="p-6 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-blue-400" />
              Modyfikatory urządzeń
            </h3>
            
            <div className="space-y-3">
              {[
                { id: 'desktop', name: 'Komputer', icon: Monitor },
                { id: 'mobile', name: 'Telefon', icon: Smartphone },
                { id: 'tablet', name: 'Tablet', icon: Tablet }
              ].map((device) => (
                <div key={device.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <device.icon className="w-4 h-4 text-white/40" />
                    <span className="text-sm text-white/80">{device.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="-50"
                      max="50"
                      value={deviceBidAdjustments[device.id]}
                      onChange={(e) => setDeviceBidAdjustments({
                        ...deviceBidAdjustments,
                        [device.id]: Number(e.target.value)
                      })}
                      className="w-24"
                    />
                    <span className={`text-sm font-medium min-w-[50px] text-right ${
                      deviceBidAdjustments[device.id] > 0 ? 'text-green-400' :
                      deviceBidAdjustments[device.id] < 0 ? 'text-red-400' :
                      'text-white/60'
                    }`}>
                      {deviceBidAdjustments[device.id] > 0 ? '+' : ''}{deviceBidAdjustments[device.id]}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Symulacja wyników */}
        <div className="space-y-4">
          <div className="p-6 rounded-xl bg-gradient-to-br from-purple-900/30 to-pink-900/30 backdrop-blur-xl border border-purple-500/20">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-400" />
              Prognoza wyników
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                <span className="text-sm text-white/60">Szacowane kliknięcia</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-white">
                    {simulationResults.estimatedClicks.toLocaleString('pl-PL')}
                  </span>
                  <TrendingUp className="w-4 h-4 text-green-400" />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                <span className="text-sm text-white/60">Szacowany koszt</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-white">
                    {simulationResults.estimatedCost.toFixed(2)} PLN
                  </span>
                  <DollarSign className="w-4 h-4 text-blue-400" />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                <span className="text-sm text-white/60">Szacowane konwersje</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-white">
                    {simulationResults.estimatedConversions}
                  </span>
                  <Target className="w-4 h-4 text-purple-400" />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                <span className="text-sm text-white/60">Prognozowany CPA</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-white">
                    {simulationResults.estimatedCPA.toFixed(2)} PLN
                  </span>
                  {simulationResults.estimatedCPA < targetCPA ? (
                    <ArrowDown className="w-4 h-4 text-green-400" />
                  ) : (
                    <ArrowUp className="w-4 h-4 text-red-400" />
                  )}
                </div>
              </div>
              
              <div className="mt-4 p-3 rounded-lg bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white/60">Poziom pewności</span>
                  <span className="text-sm font-medium text-purple-400">
                    {simulationResults.confidence.toFixed(0)}%
                  </span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${simulationResults.confidence}%` }}
                    transition={{ duration: 1 }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Modyfikatory lokalizacji */}
          <div className="p-6 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-green-400" />
              Targetowanie geograficzne
            </h3>
            
            <div className="space-y-3">
              {locationAdjustments.map((loc, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={loc.enabled}
                      onChange={(e) => {
                        const updated = [...locationAdjustments];
                        updated[idx].enabled = e.target.checked;
                        setLocationAdjustments(updated);
                      }}
                      className="rounded border-white/30"
                    />
                    <span className="text-sm text-white/80">{loc.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="-30"
                      max="30"
                      value={loc.adjustment}
                      onChange={(e) => {
                        const updated = [...locationAdjustments];
                        updated[idx].adjustment = Number(e.target.value);
                        setLocationAdjustments(updated);
                      }}
                      disabled={!loc.enabled}
                      className="w-20"
                    />
                    <span className={`text-sm font-medium min-w-[45px] text-right ${
                      !loc.enabled ? 'text-white/20' :
                      loc.adjustment > 0 ? 'text-green-400' :
                      loc.adjustment < 0 ? 'text-red-400' :
                      'text-white/60'
                    }`}>
                      {loc.adjustment > 0 ? '+' : ''}{loc.adjustment}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Harmonogram */}
      <div className="p-6 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-400" />
            Harmonogram bidów
          </h3>
          <button
            onClick={() => setScheduleEnabled(!scheduleEnabled)}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
              scheduleEnabled
                ? 'bg-green-600/20 border border-green-500/30 text-green-400'
                : 'bg-white/5 border border-white/10 text-white/60'
            }`}
          >
            {scheduleEnabled ? 'Włączony' : 'Wyłączony'}
          </button>
        </div>
        
        <div className={`grid grid-cols-7 gap-2 ${!scheduleEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
          {Object.entries(schedule).map(([day, config]) => (
            <div key={day} className="text-center">
              <p className="text-xs text-white/60 mb-2 capitalize">
                {day === 'monday' && 'Pon'}
                {day === 'tuesday' && 'Wt'}
                {day === 'wednesday' && 'Śr'}
                {day === 'thursday' && 'Czw'}
                {day === 'friday' && 'Pt'}
                {day === 'saturday' && 'Sob'}
                {day === 'sunday' && 'Ndz'}
              </p>
              <div className="p-2 rounded-lg bg-white/5 border border-white/10">
                <input
                  type="number"
                  value={config.multiplier}
                  onChange={(e) => setSchedule({
                    ...schedule,
                    [day]: { ...config, multiplier: Number(e.target.value) }
                  })}
                  step="0.1"
                  min="0.5"
                  max="2"
                  className="w-full text-center bg-transparent text-sm text-white focus:outline-none"
                />
                <p className="text-xs text-white/40 mt-1">x{config.multiplier}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Przyciski akcji */}
      <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-purple-900/30 to-pink-900/30 backdrop-blur-xl border border-purple-500/20">
        <div>
          <p className="text-sm text-white/60">Gotowe do wdrożenia</p>
          <p className="text-xs text-white/40 mt-1">
            {activeStrategy === 'auto' ? 'Strategia AI zostanie zastosowana automatycznie' : 'Ręczne ustawienia zostaną zapisane'}
          </p>
        </div>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={applyOptimization}
          disabled={loading}
          className="px-6 py-3 rounded-xl font-medium bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-2"
        >
          {loading ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Wdrażanie...
            </>
          ) : (
            <>
              <Zap className="w-5 h-5" />
              Zastosuj optymalizację
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
};

export default BidOptimizer;
